/**
 * FeedbackTabLabel.tsx — 8209-A
 *
 * Feedback tab button for the editor toolbar row 1.
 * Follows the WriteButton pattern: uses .wormhole-item / .open-wormhole / .chevron
 * CSS classes from Menu/styles.js so it looks visually consistent with Write,
 * Rewrite, Describe, and Brainstorm.
 *
 * Design ref: docs/feedback-project/designs/renderings/No Comments.png
 */
import React from "react";
import SVG from "react-inlinesvg";
import { Spinner } from "src/sudokit/components/Spinner";

import { FEEDBACK_CATEGORY_MAP } from "../../../shared/feedbackCategories";
import StickyNoteAnchor from "../../StickyTour/StickyNoteAnchor";
import { AnchorIds } from "../../StickyTour/TourTypes";

// ---------------------------------------------------------------------------
// Types
// ---------------------------------------------------------------------------

export interface FeedbackTabLabelProps {
  /** Current selected feedback category key (e.g. "developmental", "line") */
  selectedCategory: string | null;
  /** Whether the feedback panel is currently open/active */
  isActive: boolean;
  /** Whether feedback is currently running (shows spinner, disables click) */
  isLoading?: boolean;
  /** Whether a credit quote is pending, before generation starts */
  isEstimating?: boolean;
  /** Called when the main button area (not chevron) is clicked */
  onClick: () => void;
  /** Called when the chevron (dropdown) area is clicked */
  onChevronClick: (e: React.MouseEvent) => void;
  /** Called when the Stop button is clicked during loading */
  onStop?: () => void;
  /** When true (default), show the Pro tier chip for all audiences */
  showProBadge?: boolean;
  /** Disable only the main feedback action area; keep chevron active */
  isMainActionDisabled?: boolean;
  /** When false, hide the selected category subtitle for first-use/default state */
  showSelectedCategoryLabel?: boolean;
  /** Optional children rendered inside the li (e.g. FeedbackTypeSelector dropdown) */
  children?: React.ReactNode;
}

// ---------------------------------------------------------------------------
// Helpers
// ---------------------------------------------------------------------------

const DEFAULT_CATEGORY_NAME = "Developmental";
const FEEDBACK_BUTTON_ICON_PATH = "/assets/edit.svg";

/**
 * Returns the display name for a category key.
 * Falls back to "Developmental" for null/undefined, or returns the key itself
 * for custom types not in the standard category map.
 */
export function getCategoryDisplayName(categoryKey: string | null | undefined): string {
  if (!categoryKey) return DEFAULT_CATEGORY_NAME;
  const category = FEEDBACK_CATEGORY_MAP.get(categoryKey);
  if (category) return category.name;
  // Custom type — the key is the custom plugin name
  return categoryKey;
}

// ---------------------------------------------------------------------------
// Component
// ---------------------------------------------------------------------------

export const FeedbackTabLabel = React.memo(
  React.forwardRef<HTMLLIElement, FeedbackTabLabelProps>(function FeedbackTabLabel(
    {
      selectedCategory,
      isActive,
      isLoading = false,
      isEstimating = false,
      onClick,
      onChevronClick,
      onStop,
      showProBadge = true,
      isMainActionDisabled = false,
      showSelectedCategoryLabel,
      children,
    },
    ref
  ) {
    const categoryName = getCategoryDisplayName(selectedCategory);
    const shouldShowCategoryLabel = showSelectedCategoryLabel ?? true;
    const isBusy = isEstimating || isLoading;
    const isActionDisabled = isMainActionDisabled || isEstimating;

    const handleChevronClick = (e: React.MouseEvent) => {
      e.stopPropagation();
      onChevronClick(e);
    };

    const handleMainClick = (e: React.MouseEvent) => {
      e.preventDefault();
      if (isActionDisabled) return;
      if (isLoading) {
        onStop?.();
        return;
      }
      onClick();
    };

    const handleMainKeyDown = (e: React.KeyboardEvent) => {
      if (e.key === "Enter" || e.key === " ") {
        e.preventDefault();
        if (isActionDisabled) return;
        if (isLoading) {
          onStop?.();
          return;
        }
        onClick();
      }
    };

    return (
      <li className="wormhole" ref={ref} style={{ position: "relative" }}>
        <div className={`wormhole-item${isLoading ? " feedback-stop" : ""}`}>
          <button
            type="button"
            className="open-wormhole use-component-styles"
            aria-label={isEstimating ? "Estimating Feedback" : isLoading ? "Stop Feedback" : "Open Feedback"}
            aria-pressed={isActive}
            aria-busy={isBusy}
            aria-disabled={isActionDisabled}
            disabled={isActionDisabled}
            onClick={handleMainClick}
            onKeyDown={handleMainKeyDown}
            style={{
              position: "relative",
              opacity: isMainActionDisabled ? 0.55 : 1,
              cursor: isMainActionDisabled ? "not-allowed" : undefined,
            }}
          >
            <StickyNoteAnchor anchorId={AnchorIds.FEEDBACK_BUTTON} />
            {isBusy ? (
              <Spinner size="md" label={isEstimating ? "Estimating Feedback" : "Feedback running"} className="ml-0 mr-2" />
            ) : (
              <SVG src={FEEDBACK_BUTTON_ICON_PATH} className="feedback-icon" aria-hidden="true" />
            )}
            <div
              className={`label-column${
                shouldShowCategoryLabel && !isBusy
                  ? " feedback-toolbar-label-column"
                  : " feedback-toolbar-first-use-label-column"
              }`}
            >
              {isBusy ? (
                <div className="write-state">{isEstimating ? "Estimating Feedback…" : "Cancel Feedback"}</div>
              ) : (
                <>
                  <span className="feedback-toolbar-label-row feedback-toolbar-primary-row">
                    <span className="write-state">Feedback</span>
                    {showProBadge && (
                      <span className="feedback-tier-pro-chip" aria-label="Pro feature">
                        <SVG
                          src="/assets/agentic-chat-sparkle.svg"
                          className="feedback-tier-pro-chip-icon"
                          aria-hidden="true"
                        />
                        <span className="feedback-tier-pro-chip-text">Pro</span>
                      </span>
                    )}
                  </span>
                  {shouldShowCategoryLabel && <span className="feedback-toolbar-category-title">{categoryName}</span>}
                </>
              )}
            </div>
          </button>

          {!isLoading && (
            <div
              className="chevron"
              role="button"
              tabIndex={0}
              aria-label="Select Feedback Type"
              onClick={handleChevronClick}
              style={{ position: "relative" }}
              onKeyDown={(e) => {
                if (e.key === "Enter" || e.key === " ") {
                  e.preventDefault();
                  handleChevronClick(e as unknown as React.MouseEvent);
                }
              }}
            >
              <StickyNoteAnchor anchorId={AnchorIds.FEEDBACK_CHEVRON} />
              <div className="arrow">
                <SVG src="/assets/down-chevron.svg" aria-hidden="true" />
              </div>
            </div>
          )}
        </div>
        {children}
      </li>
    );
  })
);

FeedbackTabLabel.displayName = "FeedbackTabLabel";

export default FeedbackTabLabel;
